import { Badge, TextWithMentions, Typography } from "@/components/ui";
import { CreatedByUserText } from "@/components/ui/CreatedByUserText/CreatedByUserText";
import { getUsersOptions } from "@/lib/data/react-query";
import type { ContactEventLog } from "@/lib/types";
import {
  formatDiffFieldLabel,
  formatDiffValue,
  isHiddenEventLogChangeField,
} from "@/lib/utils/eventLogDiffFormat";
import {
  getEventLogDiffFromData,
  type EventLogDiff,
} from "@/lib/utils/eventUtils";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

export const ContactEventLogCard = ({ event }: { event: ContactEventLog }) => {
  const [showFieldChanges, setShowFieldChanges] = useState(false);
  const { data: users } = useQuery(getUsersOptions());
  const getUserForUserId = (id: string) => {
    const user = users?.data.find((user) => user.id === id);
    return { user };
  };

  const { currentPayload, previousPayload } = useMemo(() => {
    return {
      currentPayload: Object.fromEntries(
        Object.entries(event.changes).map(([field, change]) => [
          field,
          change.to,
        ]),
      ),
      previousPayload: Object.fromEntries(
        Object.entries(event.changes).map(([field, change]) => [
          field,
          change.from,
        ]),
      ),
    };
  }, [event]);

  const isCreateOrDeleteEvent = useMemo(() => {
    const currentChangeCount = Object.keys(currentPayload).length;
    const previousChangeCount = Object.keys(previousPayload).length;
    if (currentChangeCount === 0 && previousChangeCount === 0) {
      return true;
    }
    return (
      (currentChangeCount > 0 && previousChangeCount === 0) ||
      (currentChangeCount === 0 && previousChangeCount > 0)
    );
  }, [currentPayload, previousPayload]);

  const eventFieldChanges = useMemo<EventLogDiff[]>(() => {
    if (isCreateOrDeleteEvent) {
      return [];
    }
    return getEventLogDiffFromData(currentPayload, previousPayload).filter(
      (change) => !isHiddenEventLogChangeField(change.field),
    );
  }, [currentPayload, previousPayload, isCreateOrDeleteEvent]);

  useEffect(() => {
    setShowFieldChanges(false);
  }, [event.id]);

  const breadcrumbItems = useMemo(() => {
    const detailLabel = getContactEventDetailLabel(event);
    return detailLabel ? ["Contact", detailLabel] : ["Contact"];
  }, [event]);

  return (
    <div className="bg-card border-border/50 flex items-start space-x-3 rounded-lg border p-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <ContactBreadcrumb items={breadcrumbItems} />
        </div>
        <p className="text-muted-foreground mt-1 line-clamp-4 text-sm">
          <TextWithMentions
            text={event.message ?? ""}
            users={users?.data ?? []}
          />
        </p>
        {!isCreateOrDeleteEvent && eventFieldChanges.length > 0 && (
          <div className="mt-2 w-full">
            {!showFieldChanges ? (
              <button
                type="button"
                onClick={() => {
                  setShowFieldChanges(true);
                }}
                className="text-primary text-sm font-semibold underline-offset-2 hover:underline focus-visible:underline"
              >
                View changes
              </button>
            ) : (
              <div className="border-border/40 bg-muted/40 rounded-lg border px-3 py-2">
                <p className="text-muted-foreground text-[11px] font-semibold tracking-wide uppercase">
                  Changes
                </p>
                <div className="mt-2 space-y-2">
                  {eventFieldChanges.map((change, index) => (
                    <div
                      key={`${change.field}-${index}`}
                      className="text-muted-foreground space-y-1 text-xs"
                    >
                      <p className="text-foreground font-semibold">
                        {formatDiffFieldLabel(change.field)}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 break-words">
                        <span className="border-destructive/30 bg-destructive/10 text-destructive max-w-full rounded-full border px-2 py-0.5 font-medium break-words">
                          {formatDiffValue(change.before)}
                        </span>
                        <span className="text-muted-foreground text-[10px] tracking-wide uppercase">
                          →
                        </span>
                        <span className="max-w-full rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-medium break-words text-emerald-700">
                          {formatDiffValue(change.after)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <CreatedByUserText
          user={
            event.actor
              ? {
                  id: event.actor.id ?? "",
                  firstName: event.actor.firstName ?? "",
                  lastName: event.actor.lastName ?? "",
                }
              : getUserForUserId(event.actorUserId ?? "").user
          }
          dateTime={event.dateCreated}
          showTime
        />
      </div>
    </div>
  );
};

const ContactBreadcrumb = ({ items }: { items: string[] }) => {
  return (
    <Badge
      variant="outline"
      className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0 rounded-full border-blue-100 bg-blue-50 px-3 py-0.5 text-blue-900"
    >
      {items.map((item, index) => (
        <span key={`${item}-${index}`} className="flex items-center">
          {index > 0 && (
            <Typography className="text-muted-foreground px-1">/</Typography>
          )}
          <Typography size="sm" weight="medium" className="text-primary">
            {item}
          </Typography>
        </span>
      ))}
    </Badge>
  );
};

const getContactEventDetailLabel = (event: ContactEventLog) => {
  if (event.primarySubjectType === "contact_address") {
    return "Address";
  }
  if (event.primarySubjectType === "contact_email") {
    return "Email";
  }
  if (event.primarySubjectType === "contact_fax") {
    return "Fax";
  }
  if (event.primarySubjectType === "contact_note") {
    return "Note";
  }
  if (event.primarySubjectType === "contact_phone") {
    return "Phone";
  }
  if (event.primarySubjectType === "contact_tag_connection") {
    return "Tag";
  }
  if (event.primarySubjectType === "contact_website") {
    return "Website";
  }
  if (event.primarySubjectType === "contact_connection") {
    return "Relationship";
  }
  return undefined;
};
